import * as React from "react";
import { useSearchParams } from "react-router";

import { useGetSupplierContractFolderByFseQuery, useGetSupplierContractFolderQuery } from "@/api/suppliers";
import type { ContractSummaryStatus, SupplierFamilySummary } from "@/types/supplier";
import { isContractFamilyLink } from "@/views/drive/supplierContractFamilyNavigation";

const POLL_INTERVAL_MS = 15_000;
const LIVE_STATUSES: ReadonlySet<ContractSummaryStatus> = new Set(["in_progress", "needs_review"]);

/** A folder's supplier relation and the contract summary state read from it. */
export interface SupplierContractFolderContext {
  supplierId: string;
  relationId: string;
  folder: SupplierFamilySummary | undefined;
  status: ContractSummaryStatus;
}

/**
 * Resolves the supplier contract folder behind a drive folder and follows its contract summary.
 *
 * The Supplier Centre links to the folder with `?supplierContractFolderId=&supplierId=`; a folder
 * opened from My Folders, search or favourites carries neither, so the folder id is looked up instead.
 * Returns null while nothing is resolved, for a folder that is not a supplier contract folder, and
 * for a relation that can no longer be read (detached folder, archived supplier).
 */
export const useSupplierContractFolder = (
  folderFseId: string | undefined,
  enabled: boolean,
): SupplierContractFolderContext | null => {
  const [searchParams] = useSearchParams();
  const hasParams = isContractFamilyLink(searchParams);
  const paramSupplierId = searchParams.get("supplierId")?.trim() ?? "";
  const paramRelationId = searchParams.get("supplierContractFolderId")?.trim() ?? "";
  const lookup = useGetSupplierContractFolderByFseQuery(folderFseId ?? "", {
    skip: !enabled || !folderFseId || hasParams,
  });
  const supplierId = hasParams ? paramSupplierId : (lookup.data?.supplier_id ?? "");
  const relationId = hasParams ? paramRelationId : (lookup.data?.relation_id ?? "");
  const [isLive, setIsLive] = React.useState(false);
  const folder = useGetSupplierContractFolderQuery(
    { supplierId, familyId: relationId },
    {
      skip: !enabled || !supplierId || !relationId,
      pollingInterval: isLive ? POLL_INTERVAL_MS : 0,
      refetchOnMountOrArgChange: true,
    },
  );
  const status = folder.data?.contract_summary_status ?? "none";

  React.useEffect(() => {
    setIsLive(LIVE_STATUSES.has(status));
  }, [status]);

  // A status that can no longer be read must not linger as "being generated", nor keep the button.
  if (!enabled || !supplierId || !relationId || folder.isError) return null;
  return { supplierId, relationId, folder: folder.data, status };
};
